import { __assign, __spreadArrays } from './_virtual/_tslib.js';
import { EMPTY } from 'rxjs';
import { expand, reduce } from 'rxjs/operators';
import { PlatformEventTypes, MessageDirection } from '@ucc/messaging-platform-service';

var MESSAGING_MESSAGE_TYPE = 'SMS-MMS';
/**
 * @returns mapped conversations
 */
var convertConversationsResponseToPlatformConversations = function (ownerPhoneNumber, _a) {
    var _b;
    var items = _a.items, nextPageMarker = _a.nextPageMarker;
    return ({
        conversations: items.map(convertConversationToPlatformConversation),
        pageIdentifiers: (_b = {},
            _b[ownerPhoneNumber] = nextPageMarker,
            _b),
    });
};
var convertConversationResponseToPlatformConversation = function (_, _a) {
    var items = _a.items;
    return items.map(convertConversationToPlatformConversation)[0];
};
/**
 * @returns mapped conversation
 */
var convertConversationToPlatformConversation = function (_a) {
    var unreadMessagesCount = _a.unreadMessagesCount, lastReadMessageId = _a.lastReadMessageId, lastReadMessageTimestamp = _a.lastReadMessageTimestamp, lastMessageId = _a.lastMessageId, lastMessageTimestamp = _a.lastMessageTimestamp, lastMessage = _a.lastMessage, contactPhoneNumbers = _a.contactPhoneNumbers, ownerPhoneNumber = _a.ownerPhoneNumber, folder = _a.folder;
    return {
        conversationId: getConversationIdFromRecipients(ownerPhoneNumber, contactPhoneNumbers),
        metadata: {
            latestMessage: {
                body: { text: lastMessage.body, media: lastMessage.media },
                direction: lastMessage.direction,
                id: lastMessage.id,
                timestamp: lastMessage.timestamp,
            },
            latestMessageSeenId: lastReadMessageId,
            latestMessageSeenTimestamp: lastReadMessageTimestamp,
            originator: { id: ownerPhoneNumber },
            unreadMessagesCount: unreadMessagesCount,
            folder: folder,
        },
        recipients: contactPhoneNumbers.map(function (id) { return ({ id: id }); }),
        type: MESSAGING_MESSAGE_TYPE,
    };
};
/**
 * @returns mapped messages
 */
var getRecipientsFromConversationId = function (conversationId) {
    // eslint-disable-next-line no-useless-catch
    try {
        return JSON.parse(conversationId);
    }
    catch (e) {
        // TODO: Log the error when the logger will be here
        throw e;
    }
};
var getConversationIdFromRecipients = function (ownerPhoneNumber, contactPhoneNumbers) {
    return JSON.stringify({
        contactPhoneNumbers: contactPhoneNumbers,
        ownerPhoneNumber: ownerPhoneNumber,
    });
};
/**
 * @returns mapped message
 */
var convertMessageResponseToPlatformMessage = function (_a) {
    var items = _a.items, nextPageMarker = _a.nextPageMarker;
    return ({
        pageIdentifier: nextPageMarker,
        results: items.map(convertMessageToPlatformMessage),
    });
};
var convertMessageToPlatformMessage = function (_a) {
    var id = _a.id, body = _a.body, deletedBy = _a.deletedBy, deletedTimestamp = _a.deletedTimestamp, deliveryStatuses = _a.deliveryStatuses, media = _a.media, timestamp = _a.timestamp, authorPhoneNumber = _a.authorPhoneNumber, tag = _a.tag;
    var _b;
    return ({
        body: { text: body, media: media || [] },
        deletedBy: deletedBy,
        deletedTimestamp: deletedTimestamp,
        id: id,
        metadata: {
            correlationId: tag,
            deliveryStatuses: (_b = deliveryStatuses) === null || _b === void 0 ? void 0 : _b.map(function (status) { return ({
                recipient: status.contactPhoneNumber,
                description: status.deliveryStatusDescription.toString(),
                retryable: status.retryable,
                timestamp: status.timestamp,
            }); }),
            originator: {
                id: authorPhoneNumber,
            },
            timestamp: timestamp,
        },
    });
};
/**
 *
 * @param params callback params
 * @param pageSize number of items to retrieve
 * @param pageMarker paginated identifier
 * @param callback function to execute
 * @throws if an error is thrown by the http call
 */
var streamPages = function (params, pageSize, pageMarker, callback) {
    var itemCount = 0;
    var pageSizePerCall = Math.min(Math.max(0, pageSize), 100);
    var callParams = pageMarker !== undefined
        ? __assign(__assign({}, params), { pageMarker: pageMarker, pageSize: pageSizePerCall }) : __assign(__assign({}, params), { pageSize: pageSizePerCall });
    return callback(callParams).pipe(expand(function (_a) {
        var nextPageMarker = _a.nextPageMarker, items = _a.items;
        itemCount += items.length;
        return nextPageMarker && itemCount < pageSize
            ? callback(__assign(__assign({}, params), { pageMarker: nextPageMarker, pageSize: Math.min(pageSize - itemCount, 100) }))
            : EMPTY;
    }), reduce(function (acc, current) { return (__assign(__assign({}, current), { items: __spreadArrays(acc.items, current.items) })); }));
};
/**
 * @returns mapped event
 */
var PlatformEventMapper = {
    DELETED_CONVERSATION: PlatformEventTypes.DELETED_PLATFORM_CONVERSATION,
    DELETED_MESSAGE: PlatformEventTypes.DELETED_PLATFORM_MESSAGE,
    INCOMING_MESSAGE: PlatformEventTypes.INCOMING_PLATFORM_MESSAGE,
    INCOMING_MESSAGE_SNIPPET: PlatformEventTypes.INCOMING_PLATFORM_MESSAGE,
    OUTGOING_MESSAGE: PlatformEventTypes.OUTGOING_PLATFORM_MESSAGE,
    READ_UPDATE_CONVERSATION: PlatformEventTypes.READ_UPDATE_PLATFORM_MESSAGE,
    DELIVERY_STATUS: PlatformEventTypes.DELIVERY_STATUS,
};
var platformEventMapper = function (event) {
    switch (event.type) {
        case 'message': {
            return {
                payload: {
                    conversationId: getConversationIdFromRecipients(event.content.ownerPhoneNumber, event.content.contactPhoneNumbers),
                    conversationMetadata: {
                        contactPhoneNumbers: event.content.contactPhoneNumbers,
                        latestMessage: {
                            body: { text: event.content.body, media: event.content.media },
                            direction: event.content.direction,
                            id: event.content.id,
                            timestamp: event.content.timestamp,
                        },
                        latestMessageSeenId: event.content.direction === MessageDirection.OUT ? event.content.id : undefined,
                        latestMessageSeenTimestamp: event.content.direction === MessageDirection.OUT ? event.content.timestamp : undefined,
                        originator: { id: event.content.ownerPhoneNumber },
                        unreadMessagesCount: event.content.direction === MessageDirection.IN ? 1 : 0,
                    },
                    message: convertMessageToPlatformMessage(event.content),
                    messageType: MESSAGING_MESSAGE_TYPE,
                    recipients: event.content.contactPhoneNumbers.map(function (id) { return ({ id: id }); }),
                },
                type: event.content.direction === MessageDirection.IN
                    ? PlatformEventTypes.INCOMING_PLATFORM_MESSAGE
                    : PlatformEventTypes.OUTGOING_PLATFORM_MESSAGE,
            };
        }
        case 'conversation-update': {
            return event.content.deletedBy
                ? {
                    payload: getConversationIdFromRecipients(event.content.ownerPhoneNumber, event.content.contactPhoneNumbers),
                    type: PlatformEventTypes.DELETED_PLATFORM_CONVERSATION,
                }
                : event.content.folder
                    ? {
                        payload: {
                            conversationId: getConversationIdFromRecipients(event.content.ownerPhoneNumber, event.content.contactPhoneNumbers),
                            folder: event.content.folder,
                            messageType: MESSAGING_MESSAGE_TYPE,
                        },
                        type: PlatformEventTypes.READ_UPDATE_CONVERSATION,
                    }
                    : undefined;
        }
        case 'delivery-status':
            return {
                payload: {
                    deliveryStatusDescription: event.content.deliveryStatusDescription,
                    messageId: event.content.messageId,
                    recipient: event.content.contactPhoneNumber,
                    retryable: event.content.retryable,
                    timestamp: event.content.timestamp,
                },
                type: PlatformEventTypes.DELIVERY_STATUS,
            };
        default:
            return undefined;
    }
};
function isObject(x) {
    return typeof x === 'object';
}
var removeUndefined = function (obj) {
    if (!isObject(obj)) {
        return obj;
    }
    var newObject = Object.keys(obj).reduce(function (acc, key) {
        var _acc = acc;
        if (obj[key] !== undefined) {
            _acc[key] = obj[key];
        }
        return _acc;
    }, {});
    return newObject;
};

export { MESSAGING_MESSAGE_TYPE, PlatformEventMapper, convertConversationResponseToPlatformConversation, convertConversationToPlatformConversation, convertConversationsResponseToPlatformConversations, convertMessageResponseToPlatformMessage, convertMessageToPlatformMessage, getConversationIdFromRecipients, getRecipientsFromConversationId, platformEventMapper, removeUndefined, streamPages };
